import { lazy } from 'react';
import { Redirect } from 'react-router-dom';
import { legacyMenuConfiguration } from 'constants/menuConfiguration';
import { Route } from './routeTypes';
import Messages from 'constants/rpcTypes';

// commonly used
import Root from 'pages/root';
import Home from 'pages/home';
import GetStarted from 'pages/getStarted';
import Payments from 'pages/payments';

// light pages < 50kb
import Risk from 'pages/risks';
import Invoices from 'pages/invoices';
import Recurring from 'pages/recurring';
import References from 'pages/references';
import VirtualTerminal from 'pages/virtualTerminal';
import ManualPaymentsWrapper from 'pages/manualPayments';
import FeatureAssignmentContainer from '../pages/features/components/FeatureAssignmentContainer';
import InternalDomainContainer from '../pages/internalDomains';

// large pages > 50kb
const Payouts = lazy(() => import('pages/payouts'));
const Reports = lazy(() => import('pages/reports'));
const Finance = lazy(() => import('pages/finance'));
const Chargebacks = lazy(() => import('pages/chargebacks'));
const Profile = lazy(() => import('pages/profile'));
const Projects = lazy(() => import('pages/projects'));
const Analytics = lazy(() => import('pages/analytics'));
const Remittance = lazy(() => import('pages/remittance'));

// rarely used
const FinancialData = lazy(() => import('pages/financialData'));
const Team = lazy(() => import('pages/team'));
const UserBuilder = lazy(() => import('components/userBuilder'));
const BusinessDocuments = lazy(() => import('pages/businessDocuments'));
const Help = lazy(() => import('pages/help'));
const Reconciliation = lazy(() => import('pages/reconciliation'));
const LcManage = lazy(() => import('pages/lcmanage'));
const FeatureManage = lazy(() => import('pages/features'));
const Admin = lazy(() => import('pages/admin'));
const AdminTools = lazy(() => import('pages/adminTools'));
const ActionsPage = lazy(() => import('pages/actions'));
const Icons = lazy(() => import('pages/icons'));
const BannerConfig = lazy(() => import('pages/bannerConfig'));
const GetRegisterContainer = lazy(() => import('pages/getRegister'));
const AccountingStatementRequests = lazy(
  () => import('pages/accountingStatementRequests')
);
const Onboarding = lazy(() => import('pages/onboarding'));
const OfflineQr = lazy(() => import( 'pages/offlineQr'))
const OfflineQrDetails = lazy(() => import('pages/offlineQr/components/OfflineQrDetails'))
const OfflineQrCreate = lazy(() => import('pages/offlineQr/components/OfflineQrCreate'))

const InternalDomains = lazy(() => import('pages/internalDomains'));

export const privateRoutesConfig: Route[] = [
  {
    path: '/',
    component: Root,
    exact: true,
    params: {
      permissions: [
        Messages.Payments_List,
        Messages.Maf_Get,
        Messages.GetStarted_Project,
        Messages.Profile_Get,
        Messages.Home_View,
      ],
    },
  },
  {
    path: '/get-started',
    component: GetStarted,
    params: {
      permissions: [Messages.GetStarted_Project],
    },
  },
  {
    path: '/home',
    component: Home,
    params: {
      permissions: [Messages.Home_View],
    },
  },
  {
    path: '/profile',
    component: Profile,
    params: {
      permissions: [Messages.Profile_Get],
    },
  },
  {
    path: '/payments/:id?',
    component: Payments,
  },
  {
    path: '/payouts/:id?',
    component: Payouts,
    params: {
      permissions: [
        Messages.BulkPayouts_List,
        Messages.PaymentOperation_Payout,
        Messages.BulkPayouts_UploadPayout,
      ],
    },
  },
  {
    path: '/manual-payments/:id?',
    component: ManualPaymentsWrapper,
  },
  {
    path: '/virtual-terminal-form',
    component: VirtualTerminal,
    params: {
      permissions: [Messages.MotoPayment_Create],
    },
  },
  {
    path: '/lcmanage',
    component: LcManage,
    params: {
      permissions: [Messages.Localize_LexemesList],
    },
  },
  {
    path: '/maf/:id?',
    component: BusinessDocuments,
  },
  {
    path: '/merchant-onboarding',
    component: Onboarding,
  },
  {
    path: '/reports/create',
    component: Reports,
    params: {
      isTemplatesBuilder: true,
    },
  },
  {
    path: '/reports/:id',
    component: Reports,
    params: {
      isTemplatesBuilder: true,
    },
  },
  {
    path: '/reports/templates',
    component: Reports,
  },
  {
    path: '/reports',
    component: Reports,
  },
  {
    path: '/analytics/:slug?',
    component: Analytics,
  },
  {
    path: '/analytics',
    component: Analytics,
  },
  {
    path: '/admin',
    exact: true,
    component: Admin,
    params: {
      isAdmin: true,
      permissions: [Messages.User_List],
    },
  },
  {
    path: '/admin/create',
    component: UserBuilder,
    params: {
      isAdmin: true,
      permissions: [Messages.User_List],
    },
  },
  {
    path: '/admin/edit/:id',
    component: UserBuilder,
    params: {
      isAdmin: true,
    },
  },
  {
    path: '/team/create',
    component: UserBuilder,
  },
  {
    path: '/team/edit/:id',
    component: UserBuilder,
  },
  {
    path: '/team',
    component: Team,
  },
  {
    path: '/finance/:id/:pageId/:entityId',
    component: Finance,
  },
  {
    path: '/finance/:id/:pageId',
    component: Finance,
  },
  {
    path: '/finance/:id',
    component: Finance,
  },
  {
    path: '/finance',
    component: Finance,
  },
  {
    path: '/recurring-payments/:id?',
    component: Recurring,
  },
  {
    path: '/projects/:id?',
    component: Projects,
  },
  {
    path: '/invoices/:formId',
    component: Invoices,
    params: {
      isCreate: true,
    },
  },
  {
    path: '/invoices',
    component: Invoices,
  },
  {
    path: '/references',
    component: References,
  },
  {
    path: '/risks/:id?',
    component: Risk,
  },
  {
    path: '/chargebacks/needs-response',
    component: Chargebacks,
  },
  {
    path: '/chargebacks/pre-arbitration',
    component: Chargebacks,
  },
  {
    path: '/chargebacks/arbitration',
    component: Chargebacks,
  },
  {
    path: '/chargebacks/:id?',
    component: Chargebacks,
  },
  {
    path: '/icons',
    component: Icons,
    params: {
      permissions: [Messages.ApsIcon_List],
    },
  },
  {
    path: '/features/:id/merchants',
    component: (props) => <FeatureAssignmentContainer {...props} entityType="merchant" />,
  },
  {
    path: '/features/:id/projects',
    component: (props) => <FeatureAssignmentContainer {...props} entityType="project" />,
  },
  {
    path: '/features/:id/users',
    component: (props) => <FeatureAssignmentContainer {...props} entityType="user" />,
  },
  {
    path: '/features/:id?',
    component: FeatureManage,
  },
  {
    path: '/admin-tools',
    component: AdminTools,
  },
  {
    path: '/actions',
    component: ActionsPage,
    permissions: [Messages.Actions_GetRegister],
  },
  {
    path: '/action-get-register',
    component: GetRegisterContainer,
    permissions: [Messages.Actions_GetRegister],
  },
  {
    path: '/admin-fin-data',
    component: FinancialData,
    params: {
      permissions: [Messages.FinancialData_FileList],
    },
  },
  {
    path: '/help/:slug?',
    component: Help,
    params: {
      permissions: [Messages.Article_Menu],
    },
  },
  {
    path: '/accounting-statement-request',
    component: AccountingStatementRequests,
  },
  {
    path: '/reconciliation',
    component: Reconciliation,
  },
  {
    path: '/offline-qr/create',
    component: OfflineQrCreate,
  },
  {
    path: '/offline-qr/:id',
    component: OfflineQrDetails,
  },
  {
    path: '/offline-qr',
    component: OfflineQr,
  },
  {
    path: '/remittance/:id?/:formId?',
    component: Remittance,
  },
  {
    path: '/banner-config',
    component: BannerConfig,
    params: {
      permissions: [Messages.Home_GetBannerConfig],
    },
  },
  {
    path: '/internal-domains',
    component: InternalDomainContainer,
    params: {
      permissions: [Messages.InternalDomain_List]
    }
  },
  {
    component: () => <Redirect to='' />,
  },
];

// TODO: make one permission config without merging of menu and route configs
const setPermissions = (route: Route) => {
  const { params, path: routePath } = route;

  if (params?.permissions) {
    return params;
  }

  const paramsItem = legacyMenuConfiguration.find(({ path }) =>
    routePath?.includes(path)
  );

  return {
    ...params,
    permissions: paramsItem?.permissions,
  };
};

export const privateRoutes = privateRoutesConfig.map((route) => ({
  ...route,
  params: setPermissions(route),
}));
